import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import {
  GroupOutlinedIcon,
  VpnKeyOutlinedIcon,
  WidgetsOutlinedIcon,
  SecurityOutlinedIcon,
} from "../../utilities/icons";
import axios from "axios";
import Cookies from "js-cookie";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import {
  getUsers,
  getUsersWithAccess,
  projectsApi,
  recentActivityApi,
} from "../../apiCalls/Apicalls";
import ActivityDetailsDrawer from "./ActivityDetailsDrawer";
import { extractProjectAccessLabels } from "../../utilities/projectAccess";
import {
  activitiesCount,
  buildActivitySummary,
} from "../../utilities/activityHelpers";
import AttendanceWidget from "../Attendance/AttendanceWidget";

const statConfig = [
  {
    key: "users",
    label: "Total Users",
    trend: "Live directory",
    color: "blue",
    Icon: GroupOutlinedIcon,
  },
  {
    key: "access",
    label: "Users with Access",
    trend: "Coverage snapshot",
    color: "green",
    Icon: VpnKeyOutlinedIcon,
  },
  {
    key: "projects",
    label: "Active Projects",
    trend: "Structured workspaces",
    color: "amber",
    Icon: WidgetsOutlinedIcon,
  },
  {
    key: "roles",
    label: "Defined Roles",
    trend: "Permission groups",
    color: "violet",
    Icon: SecurityOutlinedIcon,
  },
];

function Home() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [usersWithAccessCount, setUsersWithAccessCount] = useState(0);
  const [accessLabels, setAccessLabels] = useState([]);
  const [projects, setProjects] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const authConfig = {
      headers: {
        Authorization: Cookies.get("jwtToken"),
      },
    };

    Promise.allSettled([
      axios.get(`${getUsers}?view=dashboard`),
      axios.get(`${getUsersWithAccess}?count_only=true`),
      axios.get(`${projectsApi}?view=dashboard`),
      axios.get(`${recentActivityApi}?limit=${activitiesCount}`, authConfig),
    ]).then((results) => {
      if (results[0].status === "fulfilled") {
        setUsers(results[0].value.data || []);
      }
      if (results[1].status === "fulfilled") {
        setUsersWithAccessCount(results[1].value.data?.count || 0);
      }
      if (results[2].status === "fulfilled") {
        const nextProjects = results[2].value.data || [];
        setProjects(nextProjects);
        setAccessLabels(extractProjectAccessLabels(nextProjects));
      }
      if (results[3].status === "fulfilled") {
        setRecentActivity(results[3].value.data || []);
      }
      setLoading(false);
    });
  }, []);

  const activityItems = useMemo(() => {
    return recentActivity.map(buildActivitySummary);
  }, [recentActivity]);

  const breakdown = useMemo(() => {
    const counts = users.reduce((acc, user) => {
      const department = user?.SpaceName || "Others";
      acc[department] = (acc[department] || 0) + 1;
      return acc;
    }, {});

    const total = users.length || 1;
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, count]) => ({
        name,
        count,
        percent: Math.max(10, Math.round((count / total) * 100)),
        color: "#418BFF",
      }));
  }, [users]);

  const stats = {
    users: users.length,
    access: usersWithAccessCount,
    projects: projects.length,
    roles: projects.reduce(
      (sum, project) => sum + (project.roles || []).length,
      0,
    ),
  };

  return (
    <div className="ax-page-grid ax-dashboard">
      <Box mb={2} maxWidth={480}>
        <AttendanceWidget />
      </Box>
      <div className="ax-stats-grid">
        {statConfig.map(({ key, label, trend, color, Icon }) => (
          <div key={key} className={`ax-stat-card ${color}`}>
            <div className={`ax-stat-icon ${color}`}>
              <Icon fontSize="small" />
            </div>
            <div className="ax-stat-value">
              {loading ? <CircularProgress size={22} /> : stats[key]}
            </div>
            <div className="ax-stat-label">{label}</div>
            <div className={`ax-chip ${color}`} style={{ marginTop: 10 }}>
              {trend}
            </div>
          </div>
        ))}
      </div>

      <div className="ax-overview-grid">
        <div className="ax-card">
          <div className="ax-card-header">
            <div>
              <div className="ax-card-title">Recent Activity</div>
              <Typography className="ax-muted" variant="body2" sx={{ mt: 0.5 }}>
                Unified identity events
              </Typography>
            </div>
            <Button
              size="small"
              variant="text"
              onClick={() => navigate("/dashboard/audit-logs")}
              sx={{ fontWeight: 500 }}
            >
              View All
            </Button>
          </div>
          <div className="ax-activity-list">
            {activityItems.map((item) => (
              <div className="ax-activity-item" key={item.id}>
                <div className="ax-activity-dot" />
                <div className="ax-activity-content">
                  <div>
                    <Typography
                      variant="body2"
                      sx={{
                        textTransform: "capitalize",
                        color: "var(--ax-ink)",
                        fontSize: "0.9rem",
                        fontWeight: 500,
                      }}
                    >
                      {item.text}
                    </Typography>
                    <div className="ax-activity-time">
                      {new Date(item.time).toLocaleString()}
                    </div>
                  </div>
                  <Button
                    size="small"
                    variant="text"
                    className="ax-activity-action"
                    onClick={() => setSelectedActivity(item.raw)}
                  >
                    View
                  </Button>
                </div>
              </div>
            ))}
            {!loading && activityItems.length === 0 ? (
              <Typography
                sx={{
                  color: "var(--ax-ink-3)",
                  fontSize: "0.9rem",
                  py: 4,
                  textAlign: "center",
                }}
              >
                No recent admin activity yet.
              </Typography>
            ) : null}
          </div>
        </div>

        <div className="ax-card">
          <div className="ax-card-header">
            <div>
              <div className="ax-card-title">Team Breakdown</div>
              <Typography className="ax-muted" variant="body2" sx={{ mt: 0.5 }}>
                Department distribution
              </Typography>
            </div>
          </div>

          {breakdown.length === 0 ? (
            <Box py={6} textAlign="center">
              <Typography sx={{ color: "var(--ax-ink-3)", fontSize: "0.9rem" }}>
                No users found yet.
              </Typography>
            </Box>
          ) : (
            <div className="ax-breakdown-list">
              {breakdown.map((item) => (
                <div key={item.name}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.9rem",
                        color: "var(--ax-ink)",
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: "var(--ax-ink-2)",
                        fontSize: "0.84rem",
                      }}
                    >
                      {item.count} users
                    </Typography>
                  </Stack>
                  <div className="ax-progress">
                    <span
                      style={{
                        width: `${item.percent}%`,
                        background: item.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="ax-card">
        <div className="ax-card-header">
          <div>
            <div className="ax-card-title">Access Catalog</div>
            <Typography className="ax-muted" variant="body2" sx={{ mt: 0.5 }}>
              {accessLabels.length} reusable access labels synced from projects
            </Typography>
          </div>
        </div>
        <div className="ax-inline-list">
          {accessLabels.slice(0, 18).map((item) => (
            <span className="ax-pill" key={item}>
              {item}
            </span>
          ))}
          {!loading && accessLabels.length === 0 ? (
            <Typography sx={{ color: "var(--ax-ink-3)", fontSize: "0.9rem" }}>
              No access labels available.
            </Typography>
          ) : null}
        </div>
      </div>
      <ActivityDetailsDrawer
        open={Boolean(selectedActivity)}
        activity={selectedActivity}
        onClose={() => setSelectedActivity(null)}
      />
    </div>
  );
}

export default Home;
